import Glide from '@glidejs/glide';
import { reloadAds } from '@utils/advertisement';
import { canTrack, GTMTrack } from '@utils/dataLayer';

// Definice typu pro obsluhu událostí Glide
type GlideEventHandler = (this: any, event?: any) => void;

export const Slider = (wrapperElement: HTMLElement) => {
  const sliderElement = wrapperElement.querySelector('[data-small-gallery-slider]') as HTMLElement;

  if (sliderElement) {
    return new Glide(sliderElement, {
      type: 'slider',
      perView: 1,
      focusAt: 'center',
      keyboard: true, // Povolit navigaci klávesnicí
    }).mount();
  }
};

// Funkce nyní vrací funkci pro vyčištění
const SmallGallery = (wrapperElement: HTMLElement): () => void => {
  const slider = Slider(wrapperElement);

  if (!slider) return () => {}; // Vrátit prázdnou funkci pro vyčištění, pokud se nepodaří vytvořit slider

  let position: number = slider.index;
  let lastDirection: string | null = null;
  let lastMoveSource: 'keyboard' | 'click' | 'swipe' | null = null;

  // Jednoduchá kontrola, zda je prvek viditelný ve viewportu
  const isVisible = () => {
    return wrapperElement.offsetParent !== null && 
          wrapperElement.getBoundingClientRect().top < window.innerHeight && 
          wrapperElement.getBoundingClientRect().bottom > 0;
  };

  // Obsluha kliknutí na tlačítko - pouze uložení směru
  const handleCarouselArrows = (e: Event) => {
    const arrowButton = e.currentTarget as HTMLElement;
    lastDirection = (arrowButton as HTMLButtonElement).dataset.glideDir === '>' ? 'next' : 'previous';
    lastMoveSource = 'click';
  };

  // Obsluha klávesnice - zjednodušeno na pouhou kontrolu viditelnosti
  const handleKeyboard = (e: KeyboardEvent) => {
    // Reagovat pouze pokud je galerie viditelná
    if (!isVisible()) return;
    
    // Zpracovat pouze šipky vlevo/vpravo
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    
    // Nastavit zdroj a směr přímo, není potřeba kontrola zaměření
    lastMoveSource = 'keyboard';
    lastDirection = e.key === 'ArrowRight' ? 'next' : 'previous';
  };

  slider
    .on('move', () => {
      if (slider) {
        position = slider.index + 1;
      }
    })
    // Opravit chybu TypeScriptu pomocí správné typové anotace
    .on('run.before', (function(move: any) {
      // Výchozí hodnota 'swipe', pokud není nastaveno klávesnicí nebo kliknutím
      if (!lastMoveSource) {
        lastMoveSource = 'swipe';
      }
      
      // Přepsat směr z pohybu, pokud není nastaven
      if (!lastDirection) {
        lastDirection = move.direction === '>' ? 'next' : 'previous';
      }
    }) as GlideEventHandler)
    .on('run.after', (() => {
      if (isVisible()) {
        // Vždy získat AKTUÁLNÍ pozici ze slideru
        const currentPosition = slider.index + 1;
        
        // Sledovat, pokud máme informaci o směru a galerie je viditelná
        if (lastDirection && canTrack()) {
          GTMTrack(`gallery_${lastDirection}`, {
            gallery: {
              position: currentPosition,
              source: lastMoveSource || 'unknown'
            }
          });
        }
        
        // Vždy znovu načíst reklamy
        reloadAds();
        
        // Reset pro další navigaci
        lastDirection = null;
        lastMoveSource = null;
      }
    }) as GlideEventHandler);

  const arrowButtons: NodeListOf<HTMLButtonElement> =
    wrapperElement.querySelectorAll('[data-glide-dir]');

  if (arrowButtons.length === 0) throw new Error('Gallery: desktop arrow buttons not found');

  arrowButtons.forEach((arrowButton) => {
    arrowButton.addEventListener('click', handleCarouselArrows);
  });

  // Přidat posluchač klávesových událostí na dokument
  document.addEventListener('keydown', handleKeyboard);
  
  // Přímo vrátit funkci pro vyčištění
  return () => {
    document.removeEventListener('keydown', handleKeyboard);
    arrowButtons.forEach((arrowButton) => {
      arrowButton.removeEventListener('click', handleCarouselArrows);
    });
  };
};

// Příklad použití:
export default SmallGallery;
